import cn from "classnames"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import {
    getBestKycFlow,
    kycSelectors,
    merchantSelectors,
    sharedConfigurationSelectors,
    SpinnerContainer,
    TextButton,
    TOrganizationType,
    useSelector,
} from "swiipe.portal.shared"
import { StoreState } from "../../store/StoreState"
import { checkDigitealCompanyTransferThunk, testTriggerDigitealWebhookThunk } from "../../store/thunks/digitealThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./DigitealAdminActions.scss"

export interface IDigitealAdminActions {
    webshopId: string
    className?: string
}

export const DigitealAdminActions = ({ webshopId, className }: IDigitealAdminActions) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()
    const [flowWasUpdated, setFlowWasUpdated] = useState<boolean | null>(null)
    const [wasTransferred, setWasTransferred] = useState<boolean | null>(null)
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const flows =
        useSelector((state: StoreState) => kycSelectors.flows(state, merchantDetails?.swMerchant.swMerchantId ?? "")) ?? []
    const env = useSelector(sharedConfigurationSelectors.environment)

    if (!merchantDetails || !merchantDetails.webshops.find((w) => w.webshopId === webshopId)) {
        return null
    }

    const flowApi = getBestKycFlow(flows, merchantDetails?.swMerchant.swMerchantId ?? "", [webshopId], "DigitealApi")

    const showWebhookTriggersApi =
        flowApi?.state === "Initiated" &&
        (flowApi?.subState === "AwaitingConfirmationForComplete" || flowApi?.subState === "ChangesRequiredForComplete")
    const showCheckTransferApi = flowApi?.state === "Initiated" && flowApi?.subState === "AwaitingCompanyTransferForComplete"

    const webhookTriggersFlow = env === "Production" ? null : showWebhookTriggersApi ? flowApi : null
    const checkTransferFlow = showCheckTransferApi ? flowApi : null
    if (flows && !webhookTriggersFlow && !checkTransferFlow && wasTransferred === null && flowWasUpdated === null) {
        return null
    }

    return (
        <SpinnerContainer showSpinner={!flows} minHeight={40}>
            <h5 className="mb-3 font-weight-bold">Digiteal actions:</h5>
            <div className={cn("digiteal-admin-actions", className)}>
                {flowWasUpdated !== null && (
                    <div className="digiteal-admin-actions__webhook-triggers">
                        {flowWasUpdated ? "The merchant KYC was updated" : "Something failed while updating the KYC flow"}
                    </div>
                )}
                {webhookTriggersFlow && (
                    <div className="digiteal-admin-actions__webhook-triggers">
                        <TextButton
                            className="digiteal-admin-actions__check-transfer-button"
                            text={"Mark Digiteal KYC flow as validated"}
                            textDecoration="underline"
                            onClick={async () => {
                                try {
                                    setFlowWasUpdated(null)
                                    await dispatch(
                                        testTriggerDigitealWebhookThunk(
                                            merchantDetails.swMerchant.swMerchantId,
                                            TOrganizationType.SwMerchant,
                                            "companyValidated"
                                        )
                                    )
                                    setFlowWasUpdated(true)
                                } catch {
                                    setFlowWasUpdated(false)
                                }
                            }}
                        />
                        {showWebhookTriggersApi && (
                            <TextButton
                                className="digiteal-admin-actions__check-transfer-button"
                                text={"Mark Digiteal KYC flow as rejected"}
                                textDecoration="underline"
                                onClick={async () => {
                                    try {
                                        setFlowWasUpdated(null)
                                        await dispatch(
                                            testTriggerDigitealWebhookThunk(
                                                merchantDetails.swMerchant.swMerchantId,
                                                TOrganizationType.SwMerchant,
                                                "companyRejected"
                                            )
                                        )
                                        setFlowWasUpdated(true)
                                    } catch {
                                        setFlowWasUpdated(false)
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
                {wasTransferred !== null && (
                    <div className="digiteal-admin-actions__check-transfer">
                        {wasTransferred ? "The merchant was successfully transferred" : "The merchant is not yet transferred"}
                    </div>
                )}
                {checkTransferFlow && (
                    <div className="digiteal-admin-actions__check-transfer">
                        <TextButton
                            className="digiteal-admin-actions__check-transfer-button"
                            text={"Check if company was transferred to Aera ecom and update internally"}
                            textDecoration="underline"
                            onClick={async () => {
                                try {
                                    setWasTransferred(null)
                                    const wasTransferred = await dispatch(
                                        checkDigitealCompanyTransferThunk(
                                            merchantDetails.swMerchant.swMerchantId,
                                            TOrganizationType.SwMerchant
                                        )
                                    )
                                    setWasTransferred(wasTransferred)
                                } catch {
                                    //Catch to stop showing spinner
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </SpinnerContainer>
    )
}

export default DigitealAdminActions
